import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Button, Group, Stack, Tabs } from "@mantine/core";

import { format } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterNoteDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterNoteDifference";
import { EncounterSummaryDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterSummaryDifference";
import DxDiagnosesVersionDiff from "@/encounters/components/DxPlan/DxDiagnosesVersionDiff";
import EncounterProceduresDiff from "@/encounters/components/EncounterProceduresDiff";
import classes from "@/encounters/components/EncounterTabs/EncounterTabs.module.css";
import ProceduresAmendmentDiff from "@/encounters/components/ProceduresAmendmentDiff";
import { FilesDiff } from "@/encounters/components/shared/Files";
import { PostpartumEncounterQuestionsLinkIds } from "@/encounters/constants/postpartum-encounter";
import { useEncounterDetailVersionPair } from "@/encounters/hooks/useEncounterDetailVersionPair";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import NotFound from "@/NotFound";
import PhysicalExamsDiff from "@/physical-exams/PhysicalExamsDiff/PhysicalExamsDiff";

import ActivityAndNutritionDiff from "./components/ActivityAndNutritionDiff";
import EmotionalHealthDiff from "./components/EmotionalHealthDiff";
import FeedingDiff from "./components/FeedingDiff";
import LochiaDiff from "./components/LochiaDiff";
import MedicationsAndTherapiesDiff from "./components/MedicationsAndTherapiesDiff";
import OutputsDiff from "./components/OutputsDiff";
import PostpartumDiffHeader from "./components/PostpartumDiffHeader";
import PostpartumEncounterDiff from "./components/PostpartumEncounterDiff";
import SexualActivityDiff from "./components/SexualActivityDiff";
import SleepDiff from "./components/SleepDiff";
import SocialHealthDiff from "./components/SocialHealthDiff";
import VitalsDiff from "./components/VitalsDiff";

enum PostpartumVersionDiffTabsEnum {
  SUBJECTIVE_CLINICAL_NOTES = "subjective-clinical-notes",
  OBJECTIVE_CLINICAL_NOTES = "objective-clinical-notes",
  DX_ORDERS_PLAN = "dx-orders-plan",
}

const PostpartumVersionDiff = () => {
  const { encounterId, versionId, clientId } = useParams<{
    encounterId: string;
    versionId: string;
    clientId: string;
  }>();
  const [encounter, setEncounter] = useState<Encounter>();
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();
  const [amendmentHistory, setAmendmentHistory] = useState<QuestionnaireResponse[]>();
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [activeTab, setActiveTab] = useState<PostpartumVersionDiffTabsEnum>(
    PostpartumVersionDiffTabsEnum.SUBJECTIVE_CLINICAL_NOTES,
  );
  const medplum = useMedplum();
  const navigate = useNavigate();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        if (!versionId || !encounterId) return;

        const encounter = await medplum.readResource("Encounter", encounterId);
        if (!encounter) return;

        setEncounter(encounter);

        const foundAmendmentHistory = await fetchAmendmentHistory(medplum, encounter);
        if (!foundAmendmentHistory) return;
        const currentHistoryVersionIndex = foundAmendmentHistory.findIndex(
          (questionnaireResponse) => questionnaireResponse.meta?.versionId === versionId,
        );

        if (currentHistoryVersionIndex === -1) return;

        setCurrentVersionIndex(currentHistoryVersionIndex);
        setCurrentVersion(foundAmendmentHistory[currentHistoryVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentHistoryVersionIndex + 1]);
        setAmendmentHistory(foundAmendmentHistory);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [versionId, encounterId, medplum]);

  const {
    currentEncounter,
    previousEncounter,
    isLoading: isEncounterPairLoading,
  } = useEncounterDetailVersionPair(encounterId, amendmentHistory, currentVersionIndex);

  if (isFetchingInitialData || !encounter || isEncounterPairLoading) return <Loading />;
  if (!currentVersion && !isFetchingInitialData) return <NotFound />;

  const lastAmendedBy = currentVersion?.meta?.author?.display;
  const lastAmended = currentVersion?.meta?.lastUpdated;

  const handleTabVariant = (tab: PostpartumVersionDiffTabsEnum) => {
    return activeTab === tab ? "bold" : "default";
  };

  return (
    <Stack py="md">
      <PostpartumDiffHeader questionnaireResponse={currentVersion!} />

      <Tabs
        radius="md"
        color="gray"
        variant="pills"
        value={activeTab}
        data-testid="postpartum-version-diff-tabs"
        onChange={(value) => setActiveTab(value as PostpartumVersionDiffTabsEnum)}
      >
        <Tabs.List className={classes.tabsList}>
          <Tabs.Tab
            className={classes.tab}
            data-testid="subjective-clinical-notes-tab"
            value={PostpartumVersionDiffTabsEnum.SUBJECTIVE_CLINICAL_NOTES}
          >
            <TextWrapper
              variant={handleTabVariant(PostpartumVersionDiffTabsEnum.SUBJECTIVE_CLINICAL_NOTES)}
            >
              Subjective
            </TextWrapper>
          </Tabs.Tab>
          <Tabs.Tab
            className={classes.tab}
            data-testid="objective-clinical-notes-tab"
            value={PostpartumVersionDiffTabsEnum.OBJECTIVE_CLINICAL_NOTES}
          >
            <TextWrapper
              variant={handleTabVariant(PostpartumVersionDiffTabsEnum.OBJECTIVE_CLINICAL_NOTES)}
            >
              Objective
            </TextWrapper>
          </Tabs.Tab>
          <Tabs.Tab
            className={classes.tab}
            data-testid="dx-orders-plan-tab"
            value={PostpartumVersionDiffTabsEnum.DX_ORDERS_PLAN}
          >
            <TextWrapper variant={handleTabVariant(PostpartumVersionDiffTabsEnum.DX_ORDERS_PLAN)}>
              Dx, Orders & Plan
            </TextWrapper>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={PostpartumVersionDiffTabsEnum.SUBJECTIVE_CLINICAL_NOTES}>
          <Stack py="md">
            <PostpartumEncounterDiff
              section="subjective"
              currentVersion={currentVersion}
              previousVersion={previousVersion}
              currentEncounter={currentEncounter}
              previousEncounter={previousEncounter}
            />
            <FeedingDiff currentVersionIndex={currentVersionIndex} />
            <LochiaDiff currentVersionIndex={currentVersionIndex} />
            <OutputsDiff currentVersionIndex={currentVersionIndex} />
            <ActivityAndNutritionDiff currentVersionIndex={currentVersionIndex} />
            <SleepDiff currentVersionIndex={currentVersionIndex} />
            <SocialHealthDiff currentVersionIndex={currentVersionIndex} />
            <EmotionalHealthDiff currentVersionIndex={currentVersionIndex} />
            <SexualActivityDiff currentVersionIndex={currentVersionIndex} />
            <MedicationsAndTherapiesDiff currentVersionIndex={currentVersionIndex} />
            <VitalsDiff currentVersionIndex={currentVersionIndex} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value={PostpartumVersionDiffTabsEnum.OBJECTIVE_CLINICAL_NOTES}>
          <Stack py="md">
            <PostpartumEncounterDiff
              section="objective"
              currentVersion={currentVersion}
              previousVersion={previousVersion}
              currentEncounter={currentEncounter}
              previousEncounter={previousEncounter}
            />
            <PhysicalExamsDiff
              versionId={versionId}
              encounter={encounter!}
              currentVersionIndex={currentVersionIndex}
            />
            <EncounterProceduresDiff encounter={encounter!} />
            <ProceduresAmendmentDiff versionId={versionId} encounter={encounter!} />
            <FilesDiff currentVersionIndex={currentVersionIndex} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value={PostpartumVersionDiffTabsEnum.DX_ORDERS_PLAN}>
          <Stack py="md">
            <PostpartumEncounterDiff
              section="dx-plan"
              currentVersion={currentVersion}
              previousVersion={previousVersion}
              currentEncounter={currentEncounter}
              previousEncounter={previousEncounter}
            />
            <DxDiagnosesVersionDiff
              currentVersion={currentVersion}
              previousVersion={previousVersion}
            />
            <EncounterNoteDifference
              currentVersion={currentVersion}
              previousVersion={previousVersion}
              questionLinkId={PostpartumEncounterQuestionsLinkIds.EncounterNote}
            />
            <EncounterSummaryDifference
              currentVersion={currentVersion}
              previousVersion={previousVersion}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end">
        <Stack align="flex-end">
          <Button
            variant="outline"
            data-testid="exit-amendment-view-button"
            onClick={() => navigate(`/clients/${clientId}/encounters/${encounterId}`)}
          >
            Exit Amendment View
          </Button>
          {lastAmendedBy && lastAmended && (
            <TextWrapper>
              Last amended by {lastAmendedBy} - {format(lastAmended, "MM/dd/yyyy")}
            </TextWrapper>
          )}
        </Stack>
      </Group>
    </Stack>
  );
};

export default PostpartumVersionDiff;
